import { Component, OnInit, inject, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { toast } from 'ngx-sonner';
import { firstValueFrom, debounceTime, distinctUntilChanged } from 'rxjs';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { AccessProfileUsersModal } from '../access-profile-users-modal/access-profile-users-modal';
import { ProfileSettingsModal } from '../profile-settings-modal/profile-settings-modal';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'app-access-profiles-tab',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    TranslateModule, 
    AppPrimeTable, 
    AppButton,
    AppDropdown,
    HasPermissionDirective,
    AccessProfileUsersModal, 
    ProfileSettingsModal,
    DialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule
  ],
  templateUrl: './access-profiles-tab.html',
})
export class AccessProfilesTab implements OnInit {
  private store = inject(Store);
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private perm = inject(PermissionService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canEdit(): boolean { return this.perm.canEdit(MODULE.CONFIGURACOES); }
  get canDelete(): boolean { return this.perm.canDelete(MODULE.CONFIGURACOES); }

  @ViewChild('actionsTpl', { static: true }) actionsTpl!: TemplateRef<any>;
  @ViewChild('dateTpl', { static: true }) dateTpl!: TemplateRef<any>;
  @ViewChild('nameTpl', { static: true }) nameTpl!: TemplateRef<any>;

  profiles: any[] = [];
  loading = false;
  branchId: number | null = null;
  searchControl = new FormControl('');

  columns: PrimeTableColumn[] = [
    { field: 'name', header: 'settings_profile' },
    { field: 'user_ids_count', header: 'settings_profile_users_count' },
    { field: 'creation_date', header: 'settings_profile_created_at', type: 'date' },
    { field: 'modification_date', header: 'settings_profile_updated_at', type: 'date' },
    { field: 'actions', header: 'agenda_actions', styleClass: 'w-20' }
  ];

  get templates() {
    return {
      'name': this.nameTpl,
      'actions': this.actionsTpl,
      'creation_date': this.dateTpl,
      'modification_date': this.dateTpl
    };
  }

  // Delete Confirmation Modal
  showDeleteModal = false;
  profileToDelete: any = null;

  // View Users Modal
  showViewModal = false;
  selectedProfileForView: any = null;

  // Menu State
  openMenuId: number | null = null;

  // Profile Settings Modal
  showProfileModal = false;
  selectedProfileForEdit: any = null;
  modalReadOnly = false;

  toggleMenu(id: number) {
    if (this.openMenuId === id) {
      this.openMenuId = null;
    } else {
      this.openMenuId = id;
    }
  }

  onAddProfile() {
    if (!this.canCreate) return;
    this.selectedProfileForEdit = null;
    this.showProfileModal = true;
  }

  onEdit(profile: any) {
    if (profile?.is_default) {
      // Default roles are read-only — open the permissions view instead.
      this.onViewPermissions(profile);
      return;
    }
    if (!this.canEdit) return;
    this.selectedProfileForEdit = profile;
    this.modalReadOnly = false;
    this.showProfileModal = true;
  }

  /** Open the profile-settings modal read-only (used for default roles). */
  onViewPermissions(profile: any) {
    this.selectedProfileForEdit = profile;
    this.modalReadOnly = true;
    this.showProfileModal = true;
  }

  closeProfileModal() {
    this.showProfileModal = false;
    this.selectedProfileForEdit = null;
    this.modalReadOnly = false;
  }

  onProfileSaved() {
    this.loadData(this.searchControl.value || '');
  }
  
  ngOnInit() {
    this.store.select(selectBranchId).subscribe(id => {
      this.branchId = id;
      if (id) {
        this.loadData();
      }
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.loadData(value || '');
    });
  }

  async loadData(search: string = '') {
    this.loading = true;
    try {
      this.profiles = await firstValueFrom(this.settingsService.getAccessProfiles(search, this.branchId));
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onView(profile: any) {
    this.selectedProfileForView = profile;
    this.showViewModal = true;
  }

  closeViewModal() {
    this.showViewModal = false;
    this.selectedProfileForView = null;
  }

  openDeleteModal(profile: any) {
    if (!this.canDelete) return;
    if (profile?.is_default) return; // Default roles cannot be deleted.
    this.profileToDelete = profile;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.profileToDelete = null;
  }

  async confirmDelete() {
    if (!this.canDelete) return;
    if (!this.profileToDelete) return;
    try {
      await firstValueFrom(this.settingsService.deleteAccessProfile(this.profileToDelete.id));
      toast.success(this.translate.instant('settings_profile_success_delete'));
      this.loadData(this.searchControl.value || '');
      this.closeDeleteModal();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('settings_profile_error_delete'));
    }
  }
}
